import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Panel, Badge, EmptyState } from "./BuyerPortal";
import {
    CheckCircle2, X, AlertTriangle, Clock, ExternalLink, ChevronDown, ChevronUp,
    User, Building2, MapPin, Home as HomeIcon, IndianRupee, ImageIcon, FileText,
} from "lucide-react";

const TABS = [
    { k: "pending",  l: "Pending Review", icon: Clock,         tone: "gold"  },
    { k: "approved", l: "Approved",       icon: CheckCircle2,  tone: "green" },
    { k: "rejected", l: "Rejected",       icon: AlertTriangle, tone: "red"   },
];

/**
 * Admin panel to moderate Channel-Partner-submitted properties.
 * Sits at /admin/dashboard/partner-listings.
 */
export default function PartnerListingsPanel() {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/partner-listings", { params: { status: tab } })
            .then((r) => {
                setItems(r.data.items || []);
                setCounts(r.data.counts || { pending: 0, approved: 0, rejected: 0 });
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

    const approve = async (p) => {
        try {
            await api.post(`/admin/partner-listings/${p.id}/approve`);
            toast.success(`Approved: ${p.title}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const reject = async (p) => {
        const reason = window.prompt(`Reject "${p.title}" — please share a reason so the partner can fix and re-submit:`);
        if (!reason || !reason.trim()) return;
        try {
            await api.post(`/admin/partner-listings/${p.id}/reject`, { reason: reason.trim() });
            toast.success(`Rejected: ${p.title}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="admin-partner-listings">
            <div>
                <h1 className="font-serif text-3xl md:text-4xl">Partner Submissions</h1>
                <p className="text-black/55 text-sm mt-2 max-w-2xl">
                    Properties submitted by Channel Partners. Approved listings become visible on the public site instantly.
                </p>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-black/10">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.k;
                    return (
                        <button
                            key={t.k}
                            data-testid={`admin-partner-tab-${t.k}`}
                            onClick={() => { setExpanded(null); setTab(t.k); }}
                            className={`inline-flex items-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold border-b-2 -mb-px transition-colors ${
                                active ? "border-[var(--ebg-gold)] text-black" : "border-transparent text-black/50 hover:text-black"
                            }`}
                        >
                            <Icon size={13}/> {t.l}
                            <span className={`ml-1 text-[10px] px-1.5 py-0.5 border ${active ? "border-[var(--ebg-gold)] bg-[var(--ebg-gold)]/15" : "border-black/15"}`}>
                                {counts[t.k] || 0}
                            </span>
                        </button>
                    );
                })}
            </div>

            <Panel title={`${TABS.find(t => t.k === tab).l} (${items.length})`} testid="admin-partner-list">
                {loading ? <div className="text-black/55 text-sm py-8">Loading…</div>
                    : items.length === 0 ? <EmptyState msg={`No ${tab} partner submissions.`}/>
                    : (
                    <div className="space-y-3">
                        {items.map((p) => {
                            const isOpen = expanded === p.id;
                            return (
                                <div key={p.id} className="border border-black/10 bg-white" data-testid={`admin-partner-row-${p.id}`}>
                                    {/* Row summary */}
                                    <div className="flex items-center gap-4 p-4">
                                        {p.cover_image
                                            ? <img src={p.cover_image} alt="" className="w-20 h-20 object-cover border border-black/10 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                                            : <div className="w-20 h-20 bg-black/5 border border-black/10 flex items-center justify-center text-black/30 shrink-0"><ImageIcon size={20}/></div>}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <div className="font-bold text-[15px] truncate">{p.title}</div>
                                                <Badge tone={p.approval_status === "approved" ? "green" : p.approval_status === "rejected" ? "red" : "gold"}>
                                                    {p.approval_status || "pending"}
                                                </Badge>
                                            </div>
                                            <div className="text-[12px] text-black/60 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                                <span className="inline-flex items-center gap-1"><MapPin size={11}/> {p.locality}, {p.city}</span>
                                                <span className="inline-flex items-center gap-1"><HomeIcon size={11}/> {p.type} &middot; {p.listing_type}</span>
                                                <span className="inline-flex items-center gap-1"><IndianRupee size={11}/> {p.price_display}</span>
                                                <span className="inline-flex items-center gap-1"><User size={11}/> {p.submitted_by_name || "Partner"}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {p.approval_status === "approved" && (
                                                <a href={`/properties/${p.slug || p.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ebg-gold)] hover:text-black" data-testid={`admin-view-live-${p.id}`}>
                                                    <ExternalLink size={12}/> View Live
                                                </a>
                                            )}
                                            <button
                                                onClick={() => setExpanded(isOpen ? null : p.id)}
                                                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-bold text-black/70 hover:text-black px-3 py-2"
                                                data-testid={`admin-partner-toggle-${p.id}`}
                                            >
                                                {isOpen ? <>Hide <ChevronUp size={13}/></> : <>Details <ChevronDown size={13}/></>}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Expanded details */}
                                    {isOpen && (
                                        <div className="border-t border-black/10 p-5 md:p-6 bg-black/[0.015] space-y-6" data-testid={`admin-partner-details-${p.id}`}>
                                            {/* Meta grid */}
                                            <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
                                                <Detail l="Developer" v={p.developer || "—"}/>
                                                <Detail l="Full Address" v={p.address || "—"}/>
                                                <Detail l="RERA" v={p.rera_number || "—"}/>
                                                <Detail l="Possession" v={p.possession_date || "—"}/>
                                                <Detail l="BHK / Area" v={`${p.bhk || "—"} BHK · ${p.area_sqft ? p.area_sqft + " sq.ft" : "—"}`}/>
                                                <Detail l="Bathrooms / Parking" v={`${p.bathrooms ?? "—"} / ${p.parking ?? "—"}`}/>
                                                <Detail l="Price (numeric)" v={`${p.currency} ${p.price}`}/>
                                                <Detail l="Status" v={p.status}/>
                                            </div>

                                            {/* Description */}
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50 mb-2">Description</div>
                                                <div className="text-[13px] text-black/75 leading-relaxed whitespace-pre-wrap">{p.description}</div>
                                            </div>

                                            {/* Highlights + Amenities */}
                                            {(p.highlights?.length > 0 || p.amenities?.length > 0) && (
                                                <div className="grid md:grid-cols-2 gap-6">
                                                    {p.highlights?.length > 0 && <ListBlock title="Highlights" items={p.highlights}/>}
                                                    {p.amenities?.length  > 0 && <ListBlock title="Amenities"  items={p.amenities}/>}
                                                </div>
                                            )}

                                            {/* Gallery */}
                                            {p.images?.length > 0 && (
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50 mb-2">Gallery ({p.images.length})</div>
                                                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                                                        {p.images.slice(0, 12).map((src, i) => (
                                                            <a key={i} href={src} target="_blank" rel="noreferrer" className="block aspect-square bg-black/5 overflow-hidden">
                                                                <img src={src} alt={`gallery ${i+1}`} className="w-full h-full object-cover" onError={(e) => e.currentTarget.style.display = "none"}/>
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Rejection reason display */}
                                            {p.approval_status === "rejected" && p.rejection_reason && (
                                                <div className="border border-red-300 bg-red-50 p-4 text-[13px] text-red-800">
                                                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-red-700 mb-1">Rejection Reason</div>
                                                    {p.rejection_reason}
                                                </div>
                                            )}

                                            {/* Action buttons */}
                                            {p.approval_status !== "approved" && (
                                                <div className="flex flex-wrap gap-3 pt-2 border-t border-black/10">
                                                    <button
                                                        onClick={() => approve(p)}
                                                        data-testid={`admin-partner-approve-${p.id}`}
                                                        className="inline-flex items-center gap-2 bg-emerald-700 text-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-800"
                                                    >
                                                        <CheckCircle2 size={14}/> Approve & Publish
                                                    </button>
                                                    <button
                                                        onClick={() => reject(p)}
                                                        data-testid={`admin-partner-reject-${p.id}`}
                                                        className="inline-flex items-center gap-2 border border-red-700 text-red-700 px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-red-700 hover:text-white"
                                                    >
                                                        <X size={14}/> Reject with Reason
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Panel>
        </div>
    );
}

function Detail({ l, v }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/50">{l}</div>
            <div className="text-black/80 mt-0.5">{v}</div>
        </div>
    );
}

function ListBlock({ title, items }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50 mb-2">{title}</div>
            <ul className="text-[13px] text-black/75 space-y-1 list-disc list-inside">
                {items.map((it, i) => <li key={i}>{it}</li>)}
            </ul>
        </div>
    );
}
